// Where to go after signing in, when a link sent someone to a page that needs
// an account: a handoff's "Take over this chat" link, opened on a phone that
// is signed out. Without this the sign-in form dropped them on the agent list
// and the chat they were sent to was one search away.
//
// sessionStorage, so it belongs to the tab that followed the link and is gone
// when that tab closes. Only an agent's page and a team invite link
// (/invite/<token>) are kept: anything else in storage is ignored, so it cannot
// be used to send someone off-site.

const KEY = "buildbotsnow.returnTo";
// The page someone was on when they signed out. Signing out leaves the tab on
// that page with no session, which looks exactly like following a link to it;
// remembered, it sent the NEXT account to sign in here to a bot it does not
// own ("Agent not found"). So that one page is never remembered.
const SIGNED_OUT_AT = "buildbotsnow.signedOutAt";

export function isReturnPath(path: string): boolean {
  return /^\/agents\/[A-Za-z0-9_-]+(\?[A-Za-z0-9_=&%-]*)?$/.test(path) || isInvitePath(path);
}

export function isInvitePath(path: string): boolean {
  return /^\/invite\/[A-Za-z0-9_-]{20,}$/.test(path);
}

export function rememberReturnPath(path: string): void {
  if (!isReturnPath(path)) return;
  try {
    if (sessionStorage.getItem(SIGNED_OUT_AT) === path) return;
    sessionStorage.setItem(KEY, path);
  } catch {
    // Storage blocked: they land on the agent list, as before.
  }
}

// Read without clearing: React may render the redirect more than once, and a
// read that emptied storage would send the second render to the agent list.
// clearReturnPath runs once the dashboard is actually showing.
export function peekReturnPath(): string | null {
  try {
    const path = sessionStorage.getItem(KEY);
    return path && isReturnPath(path) ? path : null;
  } catch {
    return null;
  }
}

export function clearReturnPath(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // Nothing to clear.
  }
}

// Called as someone signs out: forget any link, and never take the page they
// are leaving as one. A different link opened later in the tab still counts.
export function markSignedOut(path: string): void {
  try {
    sessionStorage.removeItem(KEY);
    sessionStorage.setItem(SIGNED_OUT_AT, path);
  } catch {
    // Storage blocked: nothing was remembered either.
  }
}

// Signed in again: the page left behind no longer needs guarding.
export function clearSignedOut(): void {
  try {
    sessionStorage.removeItem(SIGNED_OUT_AT);
  } catch {
    // Nothing to clear.
  }
}

// Whether this visit is following a team invite, on the link itself or about
// to return to it after signing in. AuthContext then holds off creating a
// workspace of their own, since they are joining someone else's.
export function arrivingOnInvite(pathname = window.location.pathname): boolean {
  return isInvitePath(pathname) || isInvitePath(peekReturnPath() ?? "");
}
